import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Copy, Database, GripVertical, Hash, Home, Plus, Sigma, Trash2, Type as TypeIcon } from "lucide-react";
import { useMemo, useState } from "react";

import { apiRequest } from "../api/client";
import { datasetApi } from "../api/dataSources";
import { Badge, Button, EmptyState, Input, SearchInput, Select, useConfirm } from "../ds";
import { CATEGORIES, VISUALS } from "./catalog";
import type { SDef, SPage } from "./model";
import type { FieldDrag } from "./autoMeasure";
import { MIME_FIELD, MIME_VISUAL } from "./StudioCanvas";

export function VisualsPanel({ onAdd }: { onAdd: (type: string) => void }) {
  const [q, setQ] = useState("");
  const list = VISUALS.filter((v) => !q || `${v.label} ${v.description}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="st-pane">
      <SearchInput value={q} onChange={setQ} placeholder="Find a visual" width="100%" />
      <div className="st-hint">Not sure which one? Click a visual to add it, or drag it onto the page. Numbers: KPI card. Trend over time: line chart. Compare groups: bar chart.</div>
      {CATEGORIES.map((cat) => {
        const items = list.filter((v) => v.category === cat);
        if (!items.length) return null;
        return (
          <div key={cat} className="st-section">
            <div className="st-section__label">{cat}</div>
            <div className="st-palette">
              {items.map((v) => (
                <button
                  key={v.type}
                  className="st-tile"
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData(MIME_VISUAL, v.type);
                    e.dataTransfer.effectAllowed = "copy";
                  }}
                  onClick={() => onAdd(v.type)}
                  title={v.description}
                >
                  <v.icon size={20} />
                  <span>{v.label}</span>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface Field { id: string; source_name: string; display_name: string; data_type: string; field_role: string; is_visible?: boolean; default_aggregation?: string | null }
interface Measure { id: string; name: string; display_name?: string | null; aggregation?: string | null; source_field?: string | null }

/** Active tables grouped by SharePoint site (v2.2 `group_name`), groups and
 * tables sorted by name. */
export function groupDatasets<T extends { name: string; is_active: boolean; group_name?: string | null }>(list: T[]): Array<[string, T[]]> {
  const groups = new Map<string, T[]>();
  for (const d of list) {
    if (!d.is_active) continue;
    const g = d.group_name || "Other";
    groups.set(g, [...(groups.get(g) ?? []), d]);
  }
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([g, items]) => [g, items.sort((a, b) => a.name.localeCompare(b.name))]);
}

export function useDatasets(mode: "admin" | "owner") {
  return useQuery({ queryKey: ["studio-datasets", mode], queryFn: () => (mode === "admin" ? datasetApi.list() : datasetApi.listMine()), staleTime: 5 * 60_000 });
}

export function useDatasetSchema(mode: "admin" | "owner", datasetId: string | null | undefined) {
  const base = mode === "admin" ? "/api/v1/admin/datasets" : "/api/v1/owner/datasets";
  const fields = useQuery({ queryKey: ["studio-fields", mode, datasetId], queryFn: () => apiRequest<Field[]>(`${base}/${datasetId}/fields`), enabled: !!datasetId, staleTime: 5 * 60_000 });
  const measures = useQuery({ queryKey: ["studio-measures", mode, datasetId], queryFn: () => apiRequest<Measure[]>(`${base}/${datasetId}/measures`), enabled: !!datasetId, staleTime: 5 * 60_000 });
  return { fields: fields.data ?? [], measures: measures.data ?? [], loading: fields.isLoading || measures.isLoading };
}

export function DataPanel({ mode, datasetId, onPickDataset, onAddField }: { mode: "admin" | "owner"; datasetId: string | null; onPickDataset: (id: string) => void; onAddField: ((f: FieldDrag) => void) | null }) {
  const datasets = useDatasets(mode);
  const [q, setQ] = useState("");
  const { fields, measures, loading } = useDatasetSchema(mode, datasetId);
  const current = datasets.data?.find((d) => d.id === datasetId);
  const dims = fields.filter((f) => f.is_visible !== false && (!q || f.display_name.toLowerCase().includes(q.toLowerCase())));
  const ms = measures.filter((m) => !q || (m.display_name ?? m.name).toLowerCase().includes(q.toLowerCase()));

  const drag = (kind: "dimension" | "measure", name: string, extra?: Partial<FieldDrag>) => (e: React.DragEvent) => {
    e.dataTransfer.setData(MIME_FIELD, JSON.stringify({ kind, name, datasetId, ...extra }));
    e.dataTransfer.effectAllowed = "copy";
  };

  return (
    <div className="st-pane">
      <div className="st-section__label">Table</div>
      <Select value={datasetId ?? ""} onChange={(e) => e.target.value && onPickDataset(e.target.value)}>
        <option value="">Choose a table…</option>
        {groupDatasets(datasets.data ?? []).map(([group, items]) => (
          <optgroup key={group} label={group}>
            {items.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </optgroup>
        ))}
      </Select>
      {current?.source_name ? <div className="ds-small ds-faint" style={{ marginTop: 6 }}>{current.group_name} · {current.source_name}</div> : null}
      {current?.description ? <div className="ds-small ds-faint" style={{ marginTop: 6 }}>{current.description}</div> : null}
      {!datasetId ? (
        <EmptyState icon={Database} title="Pick a table" description="Its columns appear here, grouped by SharePoint site. Drag a column onto a visual, or select a visual and press +." />
      ) : (
        <>
          <div style={{ margin: "12px 0 8px" }}>
            <SearchInput value={q} onChange={setQ} placeholder="Search fields" width="100%" />
          </div>
          {loading ? <div className="ds-small ds-faint">Loading fields…</div> : null}
          <div className="st-section">
            <div className="st-section__label">Values · numbers to show</div>
            {ms.length === 0 && !loading ? <div className="ds-small ds-faint">No ready-made totals yet — drop a number column on a visual and its total is made for you.</div> : null}
            {ms.map((m) => (
              <div key={m.id} className="st-field" draggable onDragStart={drag("measure", m.name)} title={`${m.aggregation ?? ""} ${m.source_field ?? ""}`.trim()}>
                <GripVertical size={12} className="ds-faint" />
                <Sigma size={14} color="var(--success)" />
                <span className="ds-grow ds-truncate">{m.display_name || m.name.replace(/[_-]+/g, " ").replace(/^./, (c) => c.toUpperCase())}</span>
                {onAddField ? <Button size="sm" variant="ghost" icon={Plus} aria-label="Add to selected visual" onClick={() => onAddField({ kind: "measure", name: m.name, datasetId: datasetId })} /> : null}
              </div>
            ))}
          </div>
          <div className="st-section">
            <div className="st-section__label">Group by · categories and labels</div>
            {dims.map((f) => (
              <div key={f.id} className="st-field" draggable onDragStart={drag("dimension", f.source_name, f.field_role === "measure" ? { numeric: true, label: f.display_name, aggregation: f.default_aggregation } : undefined)} title={f.field_role === "measure" ? "Number — drop it on Values to show its total" : undefined}>
                <GripVertical size={12} className="ds-faint" />
                {f.data_type === "number" ? <Hash size={14} className="ds-faint" /> : <TypeIcon size={14} className="ds-faint" />}
                <span className="ds-grow ds-truncate">{f.display_name}</span>
                {f.field_role === "measure" ? <Badge>num</Badge> : null}
                {onAddField ? <Button size="sm" variant="ghost" icon={Plus} aria-label="Add to selected visual" onClick={() => onAddField({ kind: "dimension", name: f.source_name, datasetId: datasetId, ...(f.field_role === "measure" ? { numeric: true, label: f.display_name, aggregation: f.default_aggregation } : {}) })} /> : null}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function PagesPanel({
  def,
  activeId,
  onSelect,
  onChange,
}: {
  def: SDef;
  activeId: string;
  onSelect: (id: string) => void;
  onChange: (next: SDef, key?: string) => void;
}) {
  const confirm = useConfirm();
  const pages = useMemo(() => [...def.pages].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)), [def.pages]);
  const set = (pgs: SPage[], extra: Partial<SDef> = {}, key?: string) => onChange({ ...def, ...extra, pages: pgs.map((p, i) => ({ ...p, order: i })) }, key);
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= pages.length) return;
    const next = [...pages];
    [next[i], next[j]] = [next[j], next[i]];
    set(next);
  };
  return (
    <div className="st-pane">
      <div className="st-hint">Each page is one screen. Use the arrows to change the order; the house icon is the page TVs open first.</div>
      <Button icon={Plus} onClick={() => {
        const id = `page-${Math.random().toString(36).slice(2, 7)}`;
        const tpl = pages.find((p) => p.id === activeId) ?? pages[0];
        set([...pages, { id, name: `Page ${pages.length + 1}`, widgets: [], canvas: tpl?.canvas, background: tpl?.background, filters: [] } as SPage]);
        onSelect(id);
      }} style={{ width: "100%", marginBottom: 12 }}>Add page</Button>
      {pages.map((p, i) => (
        <div key={p.id} className={`st-page${p.id === activeId ? " is-active" : ""}`} onClick={() => onSelect(p.id)}>
          <div className="ds-row" style={{ gap: 6 }}>
            <Input
              inputSize="sm"
              value={p.name}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => set(pages.map((x) => (x.id === p.id ? { ...x, name: e.target.value } : x)), {}, `page-name-${p.id}`)}
              aria-label="Page name"
            />
            {def.defaultPageId === p.id ? <span title="Opens first on screens"><Home size={14} color="var(--accent)" /></span> : null}
          </div>
          <div className="ds-row" style={{ gap: 2, marginTop: 6 }}>
            <Input inputSize="sm" placeholder="Section (optional)" value={p.section ?? ""} onClick={(e) => e.stopPropagation()} onChange={(e) => set(pages.map((x) => (x.id === p.id ? { ...x, section: e.target.value || undefined } : x)), {}, `page-section-${p.id}`)} aria-label="Section" style={{ flex: 1 }} />
            <Button size="sm" variant="ghost" icon={ArrowUp} aria-label="Move up" disabled={i === 0} onClick={(e) => { e.stopPropagation(); move(i, -1); }} />
            <Button size="sm" variant="ghost" icon={ArrowDown} aria-label="Move down" disabled={i === pages.length - 1} onClick={(e) => { e.stopPropagation(); move(i, 1); }} />
            <Button size="sm" variant="ghost" icon={Home} aria-label="Open first on screens" onClick={(e) => { e.stopPropagation(); onChange({ ...def, defaultPageId: p.id }); }} />
            <Button size="sm" variant="ghost" icon={Copy} aria-label="Duplicate page" onClick={(e) => {
              e.stopPropagation();
              const id = `page-${Math.random().toString(36).slice(2, 7)}`;
              const copy = { ...p, id, name: `${p.name} (copy)`, widgets: p.widgets.map((w) => ({ ...w, id: `w-${Math.random().toString(36).slice(2, 9)}` })) };
              const next = [...pages];
              next.splice(i + 1, 0, copy);
              set(next);
              onSelect(id);
            }} />
            <Button size="sm" variant="ghost" icon={Trash2} aria-label="Delete page" disabled={pages.length <= 1} onClick={async (e) => {
              e.stopPropagation();
              if (!(await confirm({ title: `Delete page “${p.name}”?`, message: `${p.widgets.length} visual(s) on it are removed from the draft. You can undo right after.`, confirmLabel: "Delete page", tone: "danger" }))) return;
              const rest = pages.filter((x) => x.id !== p.id);
              set(rest, def.defaultPageId === p.id ? { defaultPageId: rest[0].id } : {});
              if (activeId === p.id) onSelect(rest[0].id);
            }} />
          </div>
          <div className="ds-small ds-faint" style={{ marginTop: 4 }}>{p.widgets.length} visual(s)</div>
        </div>
      ))}
    </div>
  );
}
